import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, MessageCircle, CheckCircle2 } from "lucide-react";
import api, { COMPANY, IMG } from "@/lib/api";
import { useImage } from "@/context/MediaContext";
import { Section, PrimaryButton, GlassCard, Field, inputCls, fadeUp, Toast } from "@/components/ui-kit";

export default function Contact() {
  const hydImg = useImage("hyderabad", IMG.hyderabad);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toast, setToast] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.post("/contact-leads", form); setSuccess(true); }
    catch { setToast("Could not send. Please email us directly."); }
    finally { setLoading(false); setTimeout(() => setToast(""), 3500); }
  };

  return (
    <div data-testid="contact-page">
      <section className="pt-32 pb-12 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.p {...fadeUp} className="text-xs tracking-[0.3em] uppercase text-[#3B82F6]">Contact</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.05 }} className="mt-5 font-display text-5xl md:text-7xl tracking-tighter font-semibold leading-[1.02]">
            Let's talk.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} className="mt-6 max-w-2xl text-lg text-[#D1D5DB]">
            Whether you're a driver, an investor, or a curious neighbor — we'd love to hear from you.
          </motion.p>
        </div>
      </section>

      <Section>
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            {success ? (
              <GlassCard data-testid="contact-success" className="text-center py-16">
                <CheckCircle2 className="w-12 h-12 text-[#3B82F6] mx-auto" />
                <h3 className="mt-5 font-display text-2xl">Message received.</h3>
                <p className="mt-2 text-[#D1D5DB]">Thanks {form.name?.split(" ")[0] || ""}. We'll be in touch shortly.</p>
              </GlassCard>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Name"><input required className={inputCls} data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
                <Field label="Email"><input required type="email" className={inputCls} data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                <Field label="Phone"><input className={inputCls} data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
                <Field label="Subject"><input className={inputCls} data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
                <div className="md:col-span-2">
                  <Field label="Message"><textarea required rows={5} className={inputCls} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></Field>
                </div>
                <div className="md:col-span-2 flex gap-3">
                  <PrimaryButton data-testid="contact-submit" disabled={loading}>{loading ? "Sending…" : "Send message"}</PrimaryButton>
                  <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 rounded-lg text-white hover:bg-white/5">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
              </form>
            )}
          </div>

          <div className="lg:col-span-5 space-y-4">
            <GlassCard>
              <p className="text-xs tracking-[0.2em] uppercase text-[#9CA3AF]">Reach us</p>
              <div className="mt-5 space-y-4 text-[#D1D5DB]">
                <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-3 hover:text-white" data-testid="contact-phone-link">
                  <Phone className="w-4 h-4 text-[#3B82F6]" /> {COMPANY.phone}
                </a>
                <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 hover:text-white" data-testid="contact-email-link">
                  <Mail className="w-4 h-4 text-[#3B82F6]" /> {COMPANY.email}
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#3B82F6] mt-1" />
                  <span>GK Fleet Private Limited<br />Hyderabad, India</span>
                </div>
                <a href={COMPANY.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-white" data-testid="contact-whatsapp-link">
                  <MessageCircle className="w-4 h-4 text-[#3B82F6]" /> Chat on WhatsApp
                </a>
              </div>
            </GlassCard>

            <div className="rounded-2xl overflow-hidden border border-white/10 h-72 relative">
              {hydImg && (
                <img src={hydImg} alt="Hyderabad" className="absolute inset-0 w-full h-full object-cover opacity-30" />
              )}
              <iframe
                data-testid="contact-map"
                title="GK Fleet Hyderabad"
                src="https://www.google.com/maps?q=Hyderabad,India&output=embed"
                className="relative w-full h-full grayscale contrast-125"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </Section>

      <Toast msg={toast} />
    </div>
  );
}
